/**
 * The per-field rules shared by `create_deal` and `update_deal` validation.
 *
 * WHY A `gate` PARAMETER RATHER THAN TWO COPIES: the two validators were near
 * duplicates, but NOT duplicates — and the difference was invisible because it
 * lived in the shape of each `if`. Create asked `if (input.stage)`; update asked
 * `if (input.stage !== undefined)`. So `{ stage: null }` was accepted by create
 * and rejected by update, and the same split ran through currency, close_date
 * and amount. Deduping them into one function with one gate would have silently
 * changed what `create_deal` accepts.
 *
 * So the asymmetry is now a named argument instead of a punctuation difference.
 * `tests/lib/tools/deal-validation.test.ts` pins every case of it.
 */

import { isValidDealStage } from '../../types/deal-tool.types';

/** How a field decides it was "supplied" at all. */
export type PresenceGate = (value: unknown) => boolean;

/** `if (input.x !== undefined)` — an explicit null counts as supplied. */
export const whenDefined: PresenceGate = (value) => value !== undefined;

/** `if (input.x)` — null, 0 and "" all count as absent. */
export const whenTruthy: PresenceGate = (value) => Boolean(value);

/** `if (input.x !== undefined && input.x !== null)` — only null is forgiven. */
export const whenNotNullish: PresenceGate = (value) => value !== undefined && value !== null;

/** Collects one validation pass; the caller turns a non-empty list into one throw. */
export type ErrorList = string[];

export function checkAmount(value: unknown, gate: PresenceGate, errors: ErrorList): void {
  if (!gate(value)) return;
  if (typeof value !== 'number' || value <= 0) {
    errors.push('amount must be a positive number');
  }
}

export function checkCurrency(value: unknown, gate: PresenceGate, errors: ErrorList): void {
  if (!gate(value)) return;
  if (typeof value !== 'string') {
    errors.push('currency must be a string');
  }
}

export function checkStage(
  value: unknown,
  gate: PresenceGate,
  errors: ErrorList,
  allowedStages: readonly string[],
): void {
  if (!gate(value)) return;
  if (!isValidDealStage(value)) {
    errors.push(`stage must be one of: ${allowedStages.join(', ')}`);
  }
}

export function checkCloseDate(value: unknown, gate: PresenceGate, errors: ErrorList): void {
  if (!gate(value)) return;
  if (typeof value !== 'string') {
    errors.push('close_date must be a string in ISO format');
    return;
  }
  if (Number.isNaN(new Date(value).getTime())) {
    errors.push('close_date must be a valid ISO date string');
  }
}

export function checkCloseProbability(value: unknown, errors: ErrorList): void {
  if (value === undefined) return;
  if (typeof value !== 'number' || value < 0 || value > 100) {
    errors.push('close_probability must be a number between 0 and 100');
  }
}

export function checkBoolean(value: unknown, field: string, errors: ErrorList): void {
  if (value !== undefined && typeof value !== 'boolean') {
    errors.push(`${field} must be a boolean`);
  }
}

export function checkMonthlyAmount(value: unknown, errors: ErrorList): void {
  if (value === undefined) return;
  if (typeof value !== 'number' || value < 0) {
    errors.push('monthly_amount must be a non-negative number');
  }
}

export function checkStringArray(
  value: unknown,
  errors: ErrorList,
  messages: { notArray: string; notStrings: string },
): void {
  if (value === undefined) return;
  if (!Array.isArray(value)) {
    errors.push(messages.notArray);
    return;
  }
  if (!value.every((entry: unknown) => typeof entry === 'string')) {
    errors.push(messages.notStrings);
  }
}

export function checkNotes(value: unknown, errors: ErrorList): void {
  if (value !== undefined && typeof value !== 'string') {
    errors.push('notes must be a string');
  }
}

/** tags + assigned_users, whose messages are identical between create and update. */
export function checkListFields(input: Record<string, unknown>, errors: ErrorList): void {
  checkStringArray(input.tags, errors, {
    notArray: 'tags must be an array',
    notStrings: 'tags must be an array of strings',
  });
  // Hub app_users ids, resolved via find_employee.
  checkStringArray(input.assigned_users, errors, {
    notArray: 'assigned_users must be an array',
    notStrings: 'assigned_users must be an array of user id strings',
  });
}
